import { useState } from 'react';
import { Badge, Button, Chip, copyText, InfoTip, type CopyState } from '@zarya/ui';
import { dataHour } from '@app/format';
import { t } from '@app/i18n';
import type { components } from '@app/api/types';
import {
  inExecution,
  inExecutionTip,
  originLabel,
  otherPendencyTip,
  type ApprovalDetail,
  comparadaWith,
} from './ApprovalsModel';
import s from './Approvals.module.css';

/**
 * Rótulo da FONTE (P4) — de qual adapter de ingestão veio o artefato.
 *
 * O valor gravado é o nome do adapter e a palavra sai do catálogo i18n, para o
 * dado não mudar de idioma junto com a tela. Release sem fonte não existe (a
 * coluna é obrigatória desde a P4); o `undefined` cobre só o cliente de contrato
 * anterior, e cai na varredura, que é o que toda linha antiga é.
 */
function SOURCE_LABEL(source: components['schemas']['Release']['source'] | undefined): string {
  return t(`common.source.${source ?? 'registry-poll'}`);
}

/**
 * Bloco de leitura do topo de T10 (FR-059): release com o **digest completo** e
 * botão **Copiar**, commit, repositório, data de construção, origem, **fonte**,
 * identidade atribuída (ou "pendente") e o SPIFFE ID esperado.
 */
export function ReleaseHeader({ detalhe }: { detalhe: ApprovalDetail }) {
  const release = detalhe.release;
  const identidade = detalhe.identity;
  const procedencia = release?.provenance;
  const assigned = Boolean(
    identidade?.namespace && identidade.workload && identidade.image,
  );

  return (
    <section className={s.secao}>
      <h3 className={s.secaoTitulo}>
        {t('approvals.release.title')}
        {/*
          O mesmo selo da fila (29/08). Aqui ele responde à pergunta que o
          aprovador faz ao abrir o drawer de um agente com várias pendências: "é
          esta que está no ar?". Só com `true` — `false` e `null` não desenham
          nada, e `null` é «ninguém leu o cluster», não «não está rodando».
        */}
        {detalhe.running === true ? (
          <Badge tone="primary" tip={inExecutionTip()}>
            {inExecution()}
          </Badge>
        ) : null}
        {(detalhe.other_pending ?? 0) > 0 ? (
          <InfoTip
            text={otherPendencyTip()}
            label={t('approvals.otherPendency.label')}
            size="xs"
          />
        ) : null}
      </h3>
      <dl className={s.dl}>
        <dt>{t('approvals.release.digest')}</dt>
        <dd>
          <CopiavelMono texto={detalhe.release_digest ?? release?.digest ?? '—'} />
        </dd>

        <dt>{t('approvals.release.tag')}</dt>
        <dd className={s.mono}>{detalhe.release_tag ?? '—'}</dd>

        <dt>{t('approvals.release.comparedWith')}</dt>
        <dd data-testid="comparada-com">{comparadaWith(detalhe.base_contract_version)}</dd>

        <dt>{t('approvals.release.commit')}</dt>
        <dd className={s.mono}>{release?.commit ?? '—'}</dd>

        <dt>{t('approvals.release.repository')}</dt>
        <dd className={s.mono}>{procedencia?.repository ?? '—'}</dd>

        <dt>{t('approvals.release.builtAt')}</dt>
        <dd>{dataHour(procedencia?.built_at) ?? '—'}</dd>

        <dt>{t('approvals.release.origin')}</dt>
        <dd>
          <Chip>{originLabel(detalhe.origin)}</Chip>
        </dd>

        {/*
          P4 — «Origem» e «Fonte» respondem coisas diferentes, e por isso as duas
          ficam aqui, uma sob a outra: origem é o CAMINHO do produto (o Discovery
          achou um digest, ou o Access Control redeclarou o vigente); fonte é de
          que MUNDO o artefato veio, e é ela que diz quanta garantia a declaração
          tem. Quem decide sobre esta release precisa saber se a declaração estava
          dentro da imagem ou se alguém a enviou.
        */}
        <dt>{t('common.sourceLabel')}</dt>
        <dd data-testid="release-fonte">
          <Chip>{SOURCE_LABEL(release?.source)}</Chip>
          <InfoTip text={t('common.sourceTip')} label={t('common.sourceLabel')} size="xs" />
        </dd>

        {procedencia?.declared_by ? (
          <>
            <dt>{t('common.declaredBy')}</dt>
            <dd data-testid="release-declarada-por">
              {procedencia.declared_by}
              <InfoTip
                text={t('common.declaredByTip')}
                label={t('common.declaredBy')}
                size="xs"
              />
            </dd>
          </>
        ) : null}

        <dt>{t('approvals.release.assignedIdentity')}</dt>
        <dd>
          {assigned ? (
            <span className={s.mono}>
              {identidade?.namespace}/{identidade?.workload} · {identidade?.image}
            </span>
          ) : (
            <Badge tone="warning">{t('approvals.release.assignmentPending')}</Badge>
          )}
        </dd>

        <dt>{t('approvals.release.spiffeId')}</dt>
        <dd className={s.mono}>{detalhe.spiffe_id_predicted ?? '—'}</dd>
      </dl>
    </section>
  );
}

/** Texto monoespaçado com **Copiar** — o digest completo é longo demais para reler. */
export function CopiavelMono({ texto, rotulo }: { texto: string; rotulo?: string }) {
  const [copyState, setCopyState] = useState<CopyState>('idle');

  const copy = () => {
    // `copyText` recua para `execCommand` quando `navigator.clipboard` não existe
    // (contexto não seguro, e o jsdom sem stub). Falhou de verdade, o rótulo diz.
    void copyText(texto).then((ok) => setCopyState(ok ? 'done' : 'failed'));
  };

  return (
    <span className={s.digest}>
      <span className={s.mono}>{texto}</span>
      <Button variant="out" size="sm" icon="csv" onClick={copy}>
        {/* O padrão fica aqui, e não na desestruturação: um valor que vem do
            catálogo tem de ser lido a cada render. */}
        {copyState === 'failed'
          ? t('common.copyFailed')
          : copyState === 'done'
            ? t('approvals.copy.done')
            : (rotulo ?? t('approvals.copy.label'))}
      </Button>
    </span>
  );
}
